import CircularProgress from '@material-ui/core/CircularProgress';
import DialogActions from '@material-ui/core/DialogActions';
import FormControl from '@material-ui/core/FormControl';
import MenuItem from '@material-ui/core/MenuItem';
import React, { useEffect, useState } from 'react';
import Select from '@material-ui/core/Select';
import TextField from '@material-ui/core/TextField';
import CopyWithTooltip from 'components/CopyWithTooltip';
import Field from 'components/Field';
import {
  Box,
  Button,
  Dialog,
  Grid,
  theme,
} from '@attain-sre/attain-design-system';
import {
  CANCEL,
  CLIENT_NAME,
  COPY_OAUTH_URL,
  CREATE_OAUTH_URL,
  DESCRIPTION,
  OAUTH_URL,
  OAUTH_URL_CREATED_SUCCESS,
  PLATFORM,
} from '../../../content';
import { CreateOAuthUrlPayload } from '../types';
import { Formik } from 'formik';
import {
  OAUTH_AD_ACCOUNT_LABELS,
  OAUTH_PLATFORMS,
  OAUTH_PLATFORM_LABELS,
} from '../constants';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';
import { createOAuthUrl, resetCreateOAuthUrlState } from '../slice';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import { useUser } from 'reactfire';
import * as Styled from './index.styles';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      margin: theme.spacing(3),
      overflowX: 'hidden',
      '& .MuiDialog-paper': {
        overflowX: 'hidden',
      },
    },
    field: {
      marginTop: 0,
      marginBottom: 0,
      height: 48,
      '& .MuiInputBase-root': {
        height: 48,
        borderRadius: 10,
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
        height: 48,
      },
      '& .MuiSelect-root': {
        height: 48,
      },
      '& .MuiSelect-select': {
        height: 48,
        display: 'flex',
        alignItems: 'center',
      },
    },
    textareaField: {
      marginTop: 0,
      marginBottom: 0,
      '& .MuiInputBase-root': {
        minHeight: 48,
        borderRadius: 10,
      },
      '& .MuiOutlinedInput-root': {
        borderRadius: 10,
      },
    },
    gridRow: {
      marginBottom: theme.spacing(3),
    },
    labelColumn: {
      display: 'flex',
      alignItems: 'center',
      paddingRight: theme.spacing(2),
    },
    controlColumn: {
      flex: 1,
    },
    title: {
      fontStyle: 'normal',
      fontWeight: 500,
      fontSize: 23,
      paddingLeft: 16,
      paddingTop: 16,
    },
    successContainer: {
      padding: 0,
    },
    oauthUrlContainer: {
      padding: theme.spacing(2),
      backgroundColor: '#f5f5f5',
      borderRadius: 4,
      wordBreak: 'break-all',
    },
    oauthUrlText: {
      fontSize: 14,
      fontFamily: 'monospace',
    },
  })
);

interface CreateOAuthUrlModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const platformOptions = [
  {
    value: OAUTH_PLATFORMS.AMAZON,
    label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.AMAZON],
  },
  {
    value: OAUTH_PLATFORMS.REDDIT,
    label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.REDDIT],
  },
  {
    value: OAUTH_PLATFORMS.TIKTOK,
    label: OAUTH_PLATFORM_LABELS[OAUTH_PLATFORMS.TIKTOK],
  },
];

const CreateOAuthUrlModal = ({
  open,
  onClose,
  onSuccess,
}: CreateOAuthUrlModalProps) => {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const { data: user } = useUser();

  const [platform, setPlatform] = useState<'amazon' | 'reddit' | 'tiktok' | ''>(
    ''
  );
  const [clientName, setClientName] = useState('');
  const [description, setDescription] = useState('');
  const [adAccountId, setAdAccountId] = useState('');

  const createLoading = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createLoading.state === 'loading'
  );
  const createSuccess = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createSuccess
  );
  const createdOAuthUrl = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createdOAuthUrl
  );
  const createError = useAppSelector(
    (state) => state.oauthUrlsReducer.ui.createError
  );

  useEffect(() => {
    if (createSuccess && createdOAuthUrl) {
      // Success state - form will be hidden and OAuth URL shown
    }
  }, [createSuccess, createdOAuthUrl]);

  useEffect(() => {
    setAdAccountId('');
  }, [platform]);

  const handleClose = () => {
    setPlatform('');
    setClientName('');
    setDescription('');
    setAdAccountId('');
    dispatch(resetCreateOAuthUrlState());
    onClose();
  };

  const handleAdAccountIdChange = (value: string) => {
    // If Amazon is selected, only allow numbers
    if (platform === OAUTH_PLATFORMS.AMAZON) {
      // Remove any non-numeric characters
      const numericValue = value.replace(/[^0-9]/g, '');
      setAdAccountId(numericValue);
    } else {
      setAdAccountId(value);
    }
  };

  const handleSubmit = () => {
    if (!platform || !clientName || !adAccountId || !user?.email) {
      return;
    }

    const payload: CreateOAuthUrlPayload = {
      platform: platform as 'amazon' | 'reddit' | 'tiktok',
      clientName,
      description: description || undefined,
      createdBy: user.email,
    };

    if (platform === OAUTH_PLATFORMS.AMAZON) {
      // Convert to number for Amazon
      payload.amazonAdvertiserId = Number(adAccountId);
    } else if (platform === OAUTH_PLATFORMS.REDDIT) {
      payload.redditAdAccountId = adAccountId;
    } else if (platform === OAUTH_PLATFORMS.TIKTOK) {
      payload.tiktokAdvertiserId = adAccountId;
    }

    dispatch(createOAuthUrl(payload));
  };

  const getAdAccountLabel = () => {
    if (!platform) return 'Ad Account ID';
    return (
      OAUTH_AD_ACCOUNT_LABELS[
        platform as keyof typeof OAUTH_AD_ACCOUNT_LABELS
      ] || 'Ad Account ID'
    );
  };

  const isFormValid =
    platform !== '' && clientName.trim() !== '' && adAccountId.trim() !== '';
  const showSuccess = createSuccess && createdOAuthUrl;

  return (
    <Dialog
      fullWidth
      maxWidth="md"
      className={classes.root}
      title={CREATE_OAUTH_URL}
      onClose={handleClose}
      open={open}
      headerID="createOAuthUrlHeader"
      header={
        <Styled.CardHeader>
          <Styled.CardTitle>{CREATE_OAUTH_URL}</Styled.CardTitle>
        </Styled.CardHeader>
      }
      bodyID="createOAuthUrlBody"
      body={
        showSuccess ? (
          <div className={classes.successContainer}>
            <div className={classes.oauthUrlContainer}>
              <div className={classes.oauthUrlText}>
                {createdOAuthUrl?.oauthUrl}
              </div>
            </div>
          </div>
        ) : (
          <Styled.StyledBodyContainer container spacing={0}>
            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>
                  {PLATFORM} <span>*</span>
                </Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <FormControl
                  variant="outlined"
                  fullWidth
                  className={classes.field}
                >
                  <Select
                    value={platform}
                    onChange={(e: any) => setPlatform(e.target.value)}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Select Platform
                    </MenuItem>
                    {platformOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>
                  {CLIENT_NAME} <span>*</span>
                </Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <Formik>
                  <Field
                    className={classes.field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setClientName(e.target.value)
                    }
                    value={clientName}
                    fullWidth
                    id="clientName"
                    type="text"
                  />
                </Formik>
              </Grid>
            </Grid>

            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>{DESCRIPTION}</Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <TextField
                  className={classes.textareaField}
                  onChange={(
                    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
                  ) => setDescription(e.target.value)}
                  value={description}
                  fullWidth
                  id="description"
                  multiline
                  rows={3}
                  variant="outlined"
                  placeholder="Enter description (optional)"
                />
              </Grid>
            </Grid>

            <Grid container className={classes.gridRow}>
              <Grid item xs={4} className={classes.labelColumn}>
                <Styled.BodyHeader>
                  {getAdAccountLabel()} <span>*</span>
                </Styled.BodyHeader>
              </Grid>
              <Grid item xs={8} className={classes.controlColumn}>
                <Formik>
                  <Field
                    className={classes.field}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleAdAccountIdChange(e.target.value)
                    }
                    value={adAccountId}
                    fullWidth
                    id="adAccountId"
                    type="text"
                  />
                </Formik>
              </Grid>
            </Grid>
          </Styled.StyledBodyContainer>
        )
      }
      footer={
        <DialogActions>
          {showSuccess ? (
            <>
              {createdOAuthUrl?.oauthUrl && (
                <CopyWithTooltip value={createdOAuthUrl.oauthUrl}>
                  <Button color="primary" variant="contained">
                    {COPY_OAUTH_URL}
                  </Button>
                </CopyWithTooltip>
              )}
              <Button color="primary" onClick={handleClose} variant="outlined">
                {CANCEL}
              </Button>
            </>
          ) : (
            <>
              <Button color="primary" onClick={handleClose} variant="outlined">
                {CANCEL}
              </Button>
              {createLoading ? (
                <Box
                  width={142}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <CircularProgress color="primary" size={20} />
                </Box>
              ) : (
                <Button
                  color="primary"
                  disabled={!isFormValid}
                  disableElevation
                  onClick={handleSubmit}
                  variant="contained"
                >
                  Create
                </Button>
              )}
            </>
          )}
        </DialogActions>
      }
    />
  );
};

export default CreateOAuthUrlModal;
